import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextArea, RadioGroup } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

export default function CommitmentCheckPage() {
  const [commitment, setCommitment] = useState('');
  const [reason, setReason] = useState('');
  const [decision, setDecision] = useState('');

  return (
    <TaskPage
      phase="decide"
      title="Commitment Check"
      pageNumber={1}
      instruction="Before diving into prioritization, check: Are you ready to prioritize? What's driving this need?"
      whyItMatters="Not every moment requires prioritization. Sometimes the best decision is to keep doing what you're doing."
      coachRail={{
        timing: "5 minutes",
        tips: "This is about readiness, not capability. Be honest about your current state.",
        debrief: "What did you discover about your readiness to prioritize?"
      }}
    >
      <div className="space-y-6">
        {/* Current State Assessment */}
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Current State Assessment
          </h3>
          <div className="space-y-4">
            <TextArea
              label="What's your current prioritization approach?"
              placeholder="Describe how you currently decide what to work on..."
              value={commitment}
              onChange={(e) => setCommitment(e.target.value)}
              rows={4}
              helpText="Be honest about your current process (or lack thereof)"
            />
            
            <TextArea
              label="What's driving the need to prioritize now?"
              placeholder="What's happening that makes prioritization important right now?"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              helpText="Understanding the trigger helps clarify the scope"
            />
          </div>
        </div>

        {/* Decision Point */}
        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">
            Decision Point
          </h3>
          <RadioGroup
            label="Are you ready to prioritize intentionally?"
            options={[
              { 
                value: 'yes', 
                label: 'Yes - I\'m ready to commit to intentional prioritization' 
              },
              { 
                value: 'maybe', 
                label: 'Maybe - I need to understand more about what this involves' 
              },
              { 
                value: 'no', 
                label: 'No - I\'m not ready to change my current approach' 
              }
            ]}
            value={decision}
            onChange={setDecision}
            helpText="There's no wrong answer. This is about readiness, not judgment."
          />
        </div>

        {/* Next Steps Based on Decision */}
        {decision && (
          <div className="space-y-4">
            {decision === 'yes' && (
              <Callout type="tip" phase="decide">
                <div className="space-y-2">
                  <p className="font-semibold">Great! You're ready to begin.</p>
                  <p>Next: Complete the Priority Inventory to see what you're working with.</p>
                </div>
              </Callout>
            )}
            
            {decision === 'maybe' && (
              <Callout type="why" phase="decide">
                <div className="space-y-2">
                  <p className="font-semibold">That's perfectly reasonable.</p>
                  <p>Consider: What would help you feel more ready? What questions do you have about prioritization?</p>
                </div>
              </Callout>
            )}
            
            {decision === 'no' && (
              <Callout type="risk" phase="decide">
                <div className="space-y-2">
                  <p className="font-semibold">That's a valid choice.</p>
                  <p>Sometimes the best decision is to keep doing what you're doing. You can always revisit this later.</p>
                </div>
              </Callout>
            )}
          </div>
        )}

        {/* Reflection Questions */}
        <div className="bg-[var(--surface)] border border-[var(--rule)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Reflection Questions
          </h3>
          <div className="space-y-3 text-[var(--text-sec)]">
            <p>• What patterns do you notice in your current approach?</p>
            <p>• What's working well? What's not?</p>
            <p>• What would change if you prioritized more intentionally?</p>
            <p>• What concerns do you have about changing your approach?</p>
          </div>
        </div>
      </div>
    </TaskPage>
  );
}






